import { alpha, useTheme } from '@mui/material/styles'
import Skeleton from '@mui/material/Skeleton'
import {
  Client,
  type Conversation,
  type Message,
  type Participant,
} from '@twilio/conversations'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ChatConversation,
  TherapistInfo,
  PatientInfo,
} from '../services/chatApi'
import {
  listTherapistConversations,
  listPatientConversations,
  requestTherapistToken,
  requestPatientToken,
  resolveTwilioConversationSid,
  isTwilioConversationSid,
} from '../services/chatApi'
import useAuth from '@/hooks/useAuth'
import { UserRole } from '@/types'

interface ConversationListProps {
  onSelectConversation: (conversation: ChatConversation) => void
  selectedConversationSid?: string
}

const LIST_REFRESH_MS = 12000
const TYPING_TTL_MS = 6000

const getParticipantName = (participant: unknown): string => {
  if (!participant || typeof participant === 'string') {
    return 'Deleted user'
  }

  const p = participant as {
    firstName?: string
    lastName?: string
    name?: string
  }

  const first = p.firstName ?? p.name ?? ''
  const last = p.lastName ?? ''
  const fullName = `${first} ${last}`.trim()

  return fullName || 'Deleted user'
}

const getParticipantAvatar = (participant: unknown): string | undefined => {
  if (!participant || typeof participant === 'string') return undefined

  const p = participant as {
    profilePhotoUrl?: string
    avatarUrl?: string
    avatar?: string
  }

  return p.profilePhotoUrl || p.avatarUrl || p.avatar
}

const formatLastMessageTime = (iso?: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const isSameDay = now.toDateString() === date.toDateString()

  if (isSameDay) {
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const getConversationSid = (conversation: ChatConversation): string =>
  resolveTwilioConversationSid(
    conversation.conversationSid,
    conversation.conversationId
  )

interface ConversationLiveMeta {
  lastMessage?: string
  lastMessageAt?: string
  unreadCount?: number
}

export const ConversationList = ({
  onSelectConversation,
  selectedConversationSid,
}: ConversationListProps) => {
  const theme = useTheme()
  const { user } = useAuth()
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [liveMetaByConversationSid, setLiveMetaByConversationSid] = useState<
    Record<string, ConversationLiveMeta>
  >({})
  const [typingByConversationSid, setTypingByConversationSid] = useState<
    Record<string, boolean>
  >({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const typingTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({})
  const listRefreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const isTherapist = user?.role === UserRole.THERAPIST
  const userId = user?.id

  const fetchConversations = useCallback(
    async (showLoading = false) => {
      if (!userId) {
        setConversations([])
        setLoading(false)
        return
      }

      try {
        if (showLoading) {
          setLoading(true)
          setError(null)
        }

        const data = isTherapist
          ? await listTherapistConversations(userId)
          : await listPatientConversations(userId)

        setConversations(data)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load conversations'
        )
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [isTherapist, userId]
  )

  useEffect(() => {
    if (!userId) {
      setLoading(false)
      return
    }

    void fetchConversations(true)
    const interval = setInterval(() => {
      void fetchConversations(false)
    }, LIST_REFRESH_MS)

    return () => {
      clearInterval(interval)
    }
  }, [fetchConversations, userId])

  useEffect(() => {
    if (!selectedConversationSid) return
    setLiveMetaByConversationSid(prev => {
      if (!prev[selectedConversationSid]) return prev
      return {
        ...prev,
        [selectedConversationSid]: {
          ...prev[selectedConversationSid],
          unreadCount: 0,
        },
      }
    })
  }, [selectedConversationSid])

  useEffect(() => {
    if (!userId || conversations.length === 0) {
      setTypingByConversationSid({})
      return
    }

    let isActive = true
    let client: Client | null = null
    const detachFns: Array<() => void> = []

    const clearTypingForSid = (conversationSid: string) => {
      const timer = typingTimeoutsRef.current[conversationSid]
      if (timer) {
        clearTimeout(timer)
        delete typingTimeoutsRef.current[conversationSid]
      }

      setTypingByConversationSid(prev => {
        if (!prev[conversationSid]) return prev
        const next = { ...prev }
        delete next[conversationSid]
        return next
      })
    }

    const markTypingForSid = (conversationSid: string) => {
      setTypingByConversationSid(prev => ({ ...prev, [conversationSid]: true }))

      const existingTimer = typingTimeoutsRef.current[conversationSid]
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      typingTimeoutsRef.current[conversationSid] = setTimeout(() => {
        clearTypingForSid(conversationSid)
      }, TYPING_TTL_MS)
    }

    const scheduleListRefresh = () => {
      if (listRefreshDebounceRef.current) {
        clearTimeout(listRefreshDebounceRef.current)
      }
      listRefreshDebounceRef.current = setTimeout(() => {
        void fetchConversations(false)
      }, 500)
    }

    const initialiseRealtime = async () => {
      try {
        const tokenResponse = isTherapist
          ? await requestTherapistToken(userId)
          : await requestPatientToken(userId)

        if (!isActive) return

        const myIdentity = tokenResponse.identity ?? null
        client = new Client(tokenResponse.token)

        const sids = Array.from(
          new Set(
            conversations
              .map(getConversationSid)
              .filter(isTwilioConversationSid)
          )
        )

        await Promise.all(
          sids.map(async sid => {
            if (!client) return
            let twilioConversation: Conversation | null = null

            try {
              twilioConversation = await client.getConversationBySid(sid)
            } catch {
              setLiveMetaByConversationSid(prev => ({
                ...prev,
                [sid]: { ...prev[sid] },
              }))
              return
            }

            if (!twilioConversation) return

            const handleTypingStarted = (participant: Participant) => {
              if (myIdentity && participant.identity === myIdentity) return
              markTypingForSid(sid)
            }

            const handleTypingEnded = (participant: Participant) => {
              if (myIdentity && participant.identity === myIdentity) return
              clearTypingForSid(sid)
            }

            const handleMessageAdded = (message: Message) => {
              clearTypingForSid(sid)
              const body = (message.body ?? '').trim()
              const createdAt =
                message.dateCreated?.toISOString() ?? new Date().toISOString()
              const isOwnMessage = Boolean(
                myIdentity && message.author === myIdentity
              )

              setLiveMetaByConversationSid(prev => {
                const previous = prev[sid]
                const baseUnread =
                  previous?.unreadCount ??
                  (isTherapist
                    ? (conversations.find(c => getConversationSid(c) === sid)
                        ?.therapistUnread ?? 0)
                    : (conversations.find(c => getConversationSid(c) === sid)
                        ?.patientUnread ?? 0))

                const nextUnread =
                  sid === selectedConversationSid || isOwnMessage
                    ? 0
                    : baseUnread + 1

                return {
                  ...prev,
                  [sid]: {
                    ...previous,
                    lastMessage: body || previous?.lastMessage,
                    lastMessageAt: createdAt,
                    unreadCount: nextUnread,
                  },
                }
              })

              scheduleListRefresh()
            }

            const handleMessageUpdated = () => {
              scheduleListRefresh()
            }

            twilioConversation.on('typingStarted', handleTypingStarted)
            twilioConversation.on('typingEnded', handleTypingEnded)
            twilioConversation.on('messageAdded', handleMessageAdded)
            twilioConversation.on('messageUpdated', handleMessageUpdated)

            void (async () => {
              try {
                const unread = await twilioConversation.getUnreadMessagesCount()
                const paginator = await twilioConversation.getMessages(1)
                const last = paginator.items[paginator.items.length - 1]

                setLiveMetaByConversationSid(prev => ({
                  ...prev,
                  [sid]: {
                    ...prev[sid],
                    lastMessage: last?.body ?? prev[sid]?.lastMessage,
                    lastMessageAt:
                      last?.dateCreated?.toISOString() ??
                      prev[sid]?.lastMessageAt,
                    unreadCount:
                      sid === selectedConversationSid
                        ? 0
                        : (unread ?? prev[sid]?.unreadCount ?? 0),
                  },
                }))
              } catch {
                setLiveMetaByConversationSid(prev => ({
                  ...prev,
                  [sid]: { ...prev[sid] },
                }))
              }
            })()

            detachFns.push(() => {
              twilioConversation?.off('typingStarted', handleTypingStarted)
              twilioConversation?.off('typingEnded', handleTypingEnded)
              twilioConversation?.off('messageAdded', handleMessageAdded)
              twilioConversation?.off('messageUpdated', handleMessageUpdated)
            })
          })
        )
      } catch {
        // Keep list functional even if realtime listeners fail.
      }
    }

    void initialiseRealtime()

    return () => {
      isActive = false
      detachFns.forEach(detach => detach())

      Object.values(typingTimeoutsRef.current).forEach(timer => {
        clearTimeout(timer)
      })
      typingTimeoutsRef.current = {}

      if (listRefreshDebounceRef.current) {
        clearTimeout(listRefreshDebounceRef.current)
        listRefreshDebounceRef.current = null
      }

      client?.shutdown().catch(() => {
        /* noop */
      })
    }
  }, [
    conversations,
    fetchConversations,
    isTherapist,
    selectedConversationSid,
    userId,
  ])

  const orderedConversations = useMemo(
    () =>
      [...conversations].sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
        return bTime - aTime
      }),
    [conversations]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-8 h-8 border-4 rounded-full animate-spin"
          style={{
            borderColor: alpha(theme.palette.primary.main, 0.2),
            borderTopColor: theme.palette.primary.main,
          }}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
        <p>{error}</p>
      </div>
    )
  }

  if (orderedConversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-sm text-center text-slate-500">
        <p>No conversations yet</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full pb-4 overflow-y-auto">
      {orderedConversations.map(conversation => {
        const participantInfo = isTherapist
          ? conversation.patientId
          : conversation.therapistId

        const participantName = getParticipantName(participantInfo)
        const participantAvatar = getParticipantAvatar(participantInfo)

        const conversationSid = getConversationSid(conversation)
        const isSelected = conversationSid === selectedConversationSid
        const liveMeta = liveMetaByConversationSid[conversationSid]
        const unreadCount =
          liveMeta?.unreadCount ??
          (isTherapist
            ? (conversation.therapistUnread ?? 0)
            : (conversation.patientUnread ?? 0))
        const hasTyping = Boolean(typingByConversationSid[conversationSid])
        const lastMessageBody =
          liveMeta?.lastMessage ?? conversation.lastMessage ?? ''
        const isTwilioConversation = isTwilioConversationSid(conversationSid)
        const hasLiveMeta = conversationSid in liveMetaByConversationSid
        const isLoadingLastMessage = isTwilioConversation && !hasLiveMeta

        return (
          <button
            key={conversationSid || conversation._id || participantName}
            onClick={() => onSelectConversation(conversation)}
            className={`flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left transition hover:bg-slate-50 ${
              isSelected ? 'bg-indigo-50' : ''
            }`}
            style={
              isSelected
                ? { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                : undefined
            }
          >
            {participantAvatar ? (
              <img
                src={participantAvatar}
                alt={participantName}
                className="object-cover w-12 h-12 rounded-full shadow"
              />
            ) : (
              <div className="flex items-center justify-center w-12 h-12 text-sm font-semibold rounded-full shadow bg-slate-200 text-slate-600">
                {participantName && participantName.trim() !== ''
                  ? participantName
                      .split(' ')
                      .filter(Boolean)
                      .map(n => n[0] ?? '')
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                  : 'DU'}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate text-slate-800">
                {participantName}
              </p>
              {isLoadingLastMessage ? (
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '0.875rem' }}
                  width="70%"
                />
              ) : (
                <p
                  className={`text-sm truncate ${
                    hasTyping
                      ? 'text-emerald-600 font-medium'
                      : unreadCount > 0
                        ? 'text-slate-700 font-medium'
                        : 'text-slate-500'
                  }`}
                >
                  {hasTyping
                    ? 'Typing...'
                    : lastMessageBody || 'No messages yet'}
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-1">
              {(liveMeta?.lastMessageAt || conversation.lastMessageAt) && (
                <p className="text-xs text-slate-400">
                  {formatLastMessageTime(
                    liveMeta?.lastMessageAt || conversation.lastMessageAt
                  )}
                </p>
              )}
              {unreadCount > 0 && (
                <span
                  className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white rounded-full"
                  style={{ backgroundColor: theme.palette.primary.main }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
